/**
 * What the Cockpit shows, decided away from the drawing.
 *
 * The Cockpit is not a new lifecycle. Workers, terminals, plans, computers and the audit
 * trail all have their own state machines already, and this module only reads the rows
 * those machines wrote and says what one screen should make of them: a card per Worker, a
 * bounded list of what changed, and the few moments that actually need the owner.
 *
 * Pure in, pure out, so every rule can be tested without a browser. Nothing here runs
 * anything, nothing here parses terminal text, and nothing here invents a fact a row did
 * not carry - a value Brunel does not know is left out rather than guessed at.
 */
import type { AuditEvent, CommandPlanRow, NodePresence, WorkerTerminalRow } from './data';
import type { LiveStatusEvent, LiveStatusSeverity } from './live-status';
import { auditLine } from './audit';
import { durationPhrase } from './finished';
import { planLine } from './planned-run';
import { parseOfferingKey } from './offering-resolve';
import { attentionFor } from './worker-terminal-view';
import { canonicalWorkerTerminalRows } from './canonical-worker-rows';
import { recordedWorkerPhase } from './activity-cues';
import { departmentLabel } from '@brunel/shared';

/** The terminal states that are what the Worker is doing, not merely how its shell feels. */
const TERMINAL_STATES_THAT_ARE_THE_WORKERS_STATE = ['waiting_for_input', 'waiting_for_approval'];

/** How many rows the bar is allowed to carry. It is what changed, not a log viewer. */
export const COCKPIT_EVENT_LIMIT = 12;

export interface CockpitAttentionMark {
  readonly label: string;
  readonly count?: number;
}

/** One Worker, as the collapsed card reads it. Absent facts are null, never invented. */
export interface CockpitWorkerCard {
  readonly workerId: string;
  readonly workerNumber: number;
  readonly workerName: string;
  readonly status: string;
  readonly activity: string | null;
  /** The project the Worker's current session is on, when one is recorded. */
  readonly project: string | null;
  /** The terminal's working directory (usually the worktree), when one is recorded. */
  readonly folder: string | null;
  readonly runtime: string | null;
  readonly model: string | null;
  readonly machineId: string | null;
  readonly machine: string | null;
  readonly elapsed: string | null;
  readonly attention: CockpitAttentionMark | null;
  /** The terminal this Worker owns, when it has one. Never opened or closed from here. */
  readonly sessionId: string | null;
  /** #732: observed activity from the terminal's recorded last activity, separate from status. */
  readonly beacon?: WorkerActivityBeacon;
  /** The terminal row's recorded last activity, when it had one. #733 reads this; it does not poll. */
  readonly lastActivityAt?: string | null;
  /** #733 recorded phase. Null when no status, attention, or live-activity state names one. */
  readonly phase?: string | null;
  /** The department the Worker's run in progress is working as. Null when it names none. */
  readonly department?: string | null;
  /** #1133: true while this Worker's own terminal is asking the owner something. */
  readonly terminalWaiting?: boolean;
}

/** #732 activity, from canonical timestamps only: never inferred from a status word. */
export interface WorkerActivityBeacon {
  readonly kind: 'active' | 'quiet' | 'idle' | 'waiting' | 'unknown';
  readonly label: string;
}

/** Activity newer than this reads as active now. */
export const WORKER_ACTIVE_WINDOW_MS = 30_000;

const WAITING_STATES = ['waiting_for_input', 'waiting_for_approval', 'waiting', 'needs_input', 'blocked'];

export function workerActivityBeacon(
  input: { readonly status: string; readonly lastActivityAt: string | null; readonly hasTask: boolean; readonly needsOwner: boolean },
  nowMs: number,
): WorkerActivityBeacon {
  if (input.needsOwner || WAITING_STATES.includes(input.status.toLowerCase())) return { kind: 'waiting', label: 'Waiting for input' };
  const at = input.lastActivityAt === null ? Number.NaN : Date.parse(input.lastActivityAt);
  const age = Number.isNaN(at) ? null : Math.max(0, nowMs - at);
  if (age !== null && age <= WORKER_ACTIVE_WINDOW_MS && (input.hasTask || input.status.toLowerCase() !== 'idle')) {
    return { kind: 'active', label: 'Active now' };
  }
  if (!input.hasTask && ['idle', 'parked', 'offline'].includes(input.status.toLowerCase())) return { kind: 'idle', label: 'Idle' };
  if (age === null) return { kind: 'unknown', label: 'Activity unknown' };
  return { kind: 'quiet', label: `Quiet · last activity ${durationPhrase(age)} ago` };
}

/**
 * A Worker's card takes its activity time from the Worker's terminal, and a Worker in a
 * headless run has none: left alone, a Worker hard at work would read "Activity unknown".
 * So a card with a run in progress also counts the run's own recorded times - when it
 * started, and when its output was last recorded - and uses whichever is latest. Only times
 * Brunel recorded are used, and a card that is waiting on the owner is left exactly as it is.
 */
export function withRunActivity<Card extends Pick<CockpitWorkerCard, 'workerNumber' | 'status' | 'activity' | 'beacon' | 'lastActivityAt'>>(
  cards: readonly Card[],
  running: readonly { readonly worker_number: number | null; readonly assignment_id: string; readonly started_at: string | null }[],
  progress: readonly { readonly assignment_id: string; readonly observed_at: string }[],
  nowMs: number,
): readonly Card[] {
  return cards.map((card) => {
    if (card.beacon === undefined || card.beacon.kind === 'waiting') return card;
    const runs = running.filter((run) => run.worker_number === card.workerNumber);
    if (runs.length === 0) return card;
    const assignments = new Set(runs.map((run) => run.assignment_id));
    const times = [
      card.lastActivityAt ?? null,
      ...runs.map((run) => run.started_at),
      ...progress.filter((row) => assignments.has(row.assignment_id)).map((row) => row.observed_at),
    ].filter((at): at is string => at !== null && !Number.isNaN(Date.parse(at)));
    if (times.length === 0) return card;
    const latest = times.reduce((newest, at) => (Date.parse(at) > Date.parse(newest) ? at : newest));
    if (latest === card.lastActivityAt) return card;
    return {
      ...card,
      lastActivityAt: latest,
      beacon: workerActivityBeacon({ status: card.status, lastActivityAt: latest, hasTask: true, needsOwner: false }, nowMs),
    };
  });
}

/**
 * The department each Worker is working as right now: the one its run in progress names.
 * A department is a profile a job names, not something a Worker has, so a Worker with no
 * run in progress shows none, and a run that names none shows none. Never carried over
 * from an earlier run.
 */
export function withRunDepartment<Card extends Pick<CockpitWorkerCard, 'workerNumber' | 'department'>>(
  cards: readonly Card[],
  running: readonly { readonly worker_number: number | null; readonly started_at: string | null; readonly department_key?: string | null }[],
): readonly Card[] {
  return cards.map((card) => {
    // A Worker holds one run at a time; were there ever two, the newest is the one it is on.
    const run = running
      .filter((row) => row.worker_number === card.workerNumber)
      .reduce<(typeof running)[number] | null>((newest, row) =>
        newest === null || (row.started_at ?? '') > (newest.started_at ?? '') ? row : newest, null);
    const department = run === null ? null : departmentLabel(run.department_key);
    return (card.department ?? null) === department ? card : { ...card, department };
  });
}

/** #733: "last activity … ago" from one recorded timestamp. Null when nothing was recorded. */
export function workerLastActivityText(lastActivityAt: string | null | undefined, nowMs: number): string | null {
  if (lastActivityAt == null || lastActivityAt.trim() === '') return null;
  const at = Date.parse(lastActivityAt);
  if (Number.isNaN(at)) return null;
  return `last activity ${durationPhrase(Math.max(0, nowMs - at))} ago`;
}

/** #733 global dock reading. Order is active, then waiting, then quiet, then no work. */
export type BrunelActivityKind = 'active' | 'waiting' | 'quiet' | 'none';

export interface BrunelActivityCard {
  readonly beacon?: WorkerActivityBeacon;
  /** The Worker's current task title. Null means no task was recorded. */
  readonly activity: string | null;
  readonly lastActivityAt?: string | null;
}

export interface BrunelActivityIndicator {
  readonly kind: BrunelActivityKind;
  readonly label: 'Active now' | 'Waiting for you' | 'Quiet' | 'No active work';
  readonly lastActivityText: string | null;
}

const BRUNEL_ACTIVITY_LABEL: Readonly<Record<BrunelActivityKind, BrunelActivityIndicator['label']>> = {
  active: 'Active now',
  waiting: 'Waiting for you',
  quiet: 'Quiet',
  none: 'No active work',
};

function cardHasTask(card: BrunelActivityCard): boolean {
  return card.activity !== null && card.activity.trim() !== '';
}

function newestLastActivityText(cards: readonly BrunelActivityCard[], nowMs: number): string | null {
  let newestAt = Number.NEGATIVE_INFINITY;
  let newest: string | null = null;
  for (const card of cards) {
    const raw = card.lastActivityAt;
    if (raw == null || raw.trim() === '') continue;
    const at = Date.parse(raw);
    if (Number.isNaN(at) || at < newestAt) continue;
    newestAt = at;
    newest = raw;
  }
  return workerLastActivityText(newest, nowMs);
}

/**
 * One Command Center reading of the Worker cards already on screen.
 *
 * Active and waiting come straight from beacon kind. Quiet is a tasked Worker whose beacon
 * is already quiet, or whose beacon recorded no activity time: both mean the task is not
 * freshly active, and neither compares the timestamp to a second cutoff.
 */
export function brunelActivityIndicator(
  cards: readonly BrunelActivityCard[],
  nowMs: number,
): BrunelActivityIndicator {
  const kind: BrunelActivityKind = cards.some((card) => card.beacon?.kind === 'active')
    ? 'active'
    : cards.some((card) => card.beacon?.kind === 'waiting')
      ? 'waiting'
      : cards.some((card) => cardHasTask(card) && (card.beacon?.kind === 'quiet' || card.beacon?.kind === 'unknown'))
        ? 'quiet'
        : 'none';
  return {
    kind,
    label: BRUNEL_ACTIVITY_LABEL[kind],
    lastActivityText: newestLastActivityText(cards, nowMs),
  };
}

function text(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' ? null : trimmed;
}

/**
 * What one Worker is doing, in the platform's own words. A terminal that is asking the
 * owner something is the Worker's state; anything else leaves the Worker's own status to
 * speak, because a shell sitting at a prompt says nothing about the Worker.
 */
export function cockpitWorkerStatus(row: WorkerTerminalRow): string {
  const terminal = text(row.terminal_status);
  if (terminal !== null && TERMINAL_STATES_THAT_ARE_THE_WORKERS_STATE.includes(terminal)) return terminal;
  return text(row.worker_status) ?? 'unknown';
}

/**
 * #1133: true while a Worker's own live terminal is asking the owner something - a permission
 * to give, a value to type. Only the owner can answer it, and only in that terminal, so the
 * Worker's card marks it and its surface shows the terminal whatever the debug switch says.
 * A terminal that has ended, or a Worker with none, is not waiting on anybody.
 */
export function terminalWaitingOnOwner(
  row: Pick<WorkerTerminalRow, 'session_id' | 'terminal_status' | 'closed_at' | 'failure'>,
): boolean {
  if (row.session_id === null || row.closed_at !== null || row.failure !== null) return false;
  return row.terminal_status !== null && TERMINAL_STATES_THAT_ARE_THE_WORKERS_STATE.includes(row.terminal_status);
}

/** How long the Worker's current task has been running, when a start time was recorded. */
export function cockpitElapsed(row: WorkerTerminalRow, nowMs: number): string | null {
  const started = row.task_started_at === null ? Number.NaN : Date.parse(row.task_started_at);
  if (Number.isNaN(started)) return null;
  return durationPhrase(Math.max(0, nowMs - started));
}

/** What is waiting on the owner for this Worker, counted from rows rather than guessed. */
export function cockpitWorkerAttentionMark(
  row: WorkerTerminalRow,
  pendingApprovals: number,
): CockpitAttentionMark | null {
  if (pendingApprovals > 0) return { label: 'Approval needed', count: pendingApprovals };
  if (row.terminal_status === 'waiting_for_approval') return { label: 'Waiting for approval' };
  if (row.terminal_status === 'waiting_for_input') return { label: 'Waiting for input' };
  return null;
}

/** Every Worker as a card, in Worker number order - the order the owner counts them in. */
export function cockpitWorkerCards(
  rows: readonly WorkerTerminalRow[],
  options: { readonly pendingByWorker: ReadonlyMap<string, number>; readonly nowMs: number },
): readonly CockpitWorkerCard[] {
  // #879: one card per canonical Worker, never one per joined terminal/session row.
  return canonicalWorkerTerminalRows(rows)
    .map((row) => {
      const offering = parseOfferingKey(row.offering_key);
      const attention = cockpitWorkerAttentionMark(row, options.pendingByWorker.get(row.worker_id) ?? 0);
      const status = cockpitWorkerStatus(row);
      return {
      workerId: row.worker_id,
      workerNumber: row.worker_number,
      workerName: row.worker_name,
      status,
      activity: text(row.task_title),
      project: text(row.project_name),
      folder: text(row.cwd),
      runtime: offering.runtimeKey,
      model: offering.modelKey,
      machineId: row.node_id,
      machine: text(row.node_name),
      elapsed: cockpitElapsed(row, options.nowMs),
      attention,
      sessionId: row.session_id,
      phase: recordedWorkerPhase({ status, attention, liveState: null }),
      beacon: workerActivityBeacon({
        status,
        lastActivityAt: row.last_activity_at,
        hasTask: text(row.task_title) !== null,
        needsOwner: row.input_held === true || attention !== null,
      }, options.nowMs),
      lastActivityAt: row.last_activity_at,
      terminalWaiting: terminalWaitingOnOwner(row),
      };
    });
}

/**
 * One moment where a Worker needs the owner, in the Owner Attention panel's own vocabulary.
 *
 * `takeover` says whether the keyboard can honestly be taken back: it can when a terminal
 * exists, and it is the existing release path that takes it, never a second one.
 */
export interface CockpitWorkerAttention {
  readonly workerId: string;
  readonly workerNumber: number;
  readonly sessionId: string | null;
  readonly kind: 'failed_intervention_required' | 'approval_required' | 'worker_waiting_for_input' | 'owner_takeover_available';
  readonly title: string;
  readonly workerLabel: string;
  readonly outcomeLabel: string | null;
  readonly explanation: string | null;
  readonly takeover: boolean;
}

/**
 * The Workers that need the owner, one item each, most serious state first.
 *
 * A tool asking for permission inside the terminal is shown but never answered here: the
 * panel offers the Worker and the keyboard, and the owner answers it in the terminal.
 */
export function cockpitWorkerAttentions(
  rows: readonly WorkerTerminalRow[],
): readonly CockpitWorkerAttention[] {
  const items: CockpitWorkerAttention[] = [];
  for (const row of canonicalWorkerTerminalRows(rows)) {
    if (row.session_id === null) continue;
    const label = `Worker ${row.worker_number} · ${row.worker_name}`;
    const machine = text(row.node_name);
    const holder = text(row.input_holder_controller_name);
    const state = attentionFor(row);
    const common = {
      workerId: row.worker_id,
      workerNumber: row.worker_number,
      sessionId: row.session_id,
      workerLabel: label,
      outcomeLabel: machine,
    };

    if (row.failure !== null || row.closed_at !== null) {
      items.push({
        ...common, kind: 'failed_intervention_required', title: state.line, takeover: false,
        explanation: 'Nothing is running in this terminal any more.',
      });
      continue;
    }
    if (row.terminal_status === 'waiting_for_approval') {
      items.push({
        ...common, kind: 'approval_required', title: state.line, takeover: true,
        explanation: 'Brunel does not answer a tool permission question for you. Take the keyboard and answer it in the terminal.',
      });
      continue;
    }
    if (row.terminal_status === 'waiting_for_input') {
      items.push({ ...common, kind: 'worker_waiting_for_input', title: state.line, takeover: true, explanation: null });
      continue;
    }
    if (row.input_held === true && holder !== null) {
      items.push({
        ...common, kind: 'owner_takeover_available', title: `${holder} is holding this Worker's keyboard.`,
        takeover: true, explanation: 'Taking it back stops that controller typing; the Worker and its terminal keep running.',
      });
    }
  }
  return items;
}

/** What a plan's own status means to a bar that only says what changed. */
function planSeverity(status: string, requiresApproval: boolean): LiveStatusSeverity {
  switch (status) {
    case 'proposed': return requiresApproval ? 'approval' : 'working';
    case 'drafting':
    case 'sent':
    case 'evaluating': return 'working';
    case 'completed': return 'success';
    case 'needs_owner': return 'approval';
    case 'failed': return 'error';
    case 'withdrawn': return 'info';
    default: return 'info';
  }
}

/** The last time anybody wrote to this plan, which is when it last changed. */
function planAt(plan: CommandPlanRow): string {
  return plan.evaluated_at ?? plan.sent_at ?? plan.drafted_at ?? plan.created_at;
}

function millis(at: string): number {
  const parsed = Date.parse(at);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export interface CockpitEventSources {
  /** Plans, which are how an instruction moves. */
  readonly plans: readonly CommandPlanRow[];
  /** Worker terminal rows, which say what each terminal is waiting for. */
  readonly workers: readonly WorkerTerminalRow[];
  /** The computers the screen has already worked out are out of touch. */
  readonly nodesOutOfTouch: readonly NodePresence[];
  /** The audit trail, already filtered of routine check-ins by the caller. */
  readonly audit: readonly AuditEvent[];
  readonly limit?: number;
}

/**
 * What changed, newest first, from rows Brunel already recorded.
 *
 * Every event here comes from a structured source - a plan row, a terminal row, a node's
 * presence, an audit event. Nothing is read out of rendered terminal text, and nothing is
 * manufactured to fill the bar: a quiet system produces an empty list and says so.
 */
export function cockpitLiveStatusEvents(sources: CockpitEventSources): readonly LiveStatusEvent[] {
  const events: LiveStatusEvent[] = [];

  for (const plan of sources.plans) {
    const at = planAt(plan);
    events.push({
      id: `plan:${plan.id}:${at}`,
      at,
      severity: planSeverity(plan.status, plan.requires_approval),
      message: planLine(plan),
      source: 'Plan',
      worker: { id: plan.worker_id, number: plan.worker_number, name: plan.worker_name },
    });
  }

  for (const row of sources.workers) {
    if (row.session_id === null) continue;
    const at = row.last_activity_at ?? row.opened_at;
    if (at === null) continue;
    const state = attentionFor(row);
    if (state.tone === 'quiet') continue;
    const severity: LiveStatusSeverity = state.tone === 'error'
      ? 'error'
      : state.tone === 'working'
        ? 'working'
        : row.terminal_status === 'waiting_for_approval' ? 'approval' : 'waiting';
    events.push({
      id: `terminal:${row.session_id}:${at}`,
      at,
      severity,
      message: state.line,
      source: 'Terminal',
      worker: { id: row.worker_id, number: row.worker_number, name: row.worker_name },
    });
  }

  for (const node of sources.nodesOutOfTouch) {
    const at = node.last_seen_at;
    if (at === null) continue;
    events.push({
      id: `node:${node.node_id}:${at}`,
      at,
      severity: 'disconnected',
      message: `${node.name} is out of touch.`,
      source: 'Computer',
      worker: null,
    });
  }

  for (const event of sources.audit) {
    events.push({
      id: `audit:${event.id}`,
      at: event.occurred_at,
      severity: 'info',
      message: auditLine(event).sentence,
      source: 'Audit',
      worker: null,
    });
  }

  const limit = sources.limit ?? COCKPIT_EVENT_LIMIT;
  return events
    .sort((left, right) => millis(right.at) - millis(left.at))
    .slice(0, Math.max(1, limit));
}

/** How many things are waiting on the owner, counted once each. */
export function cockpitAttentionCount(input: {
  readonly pendingApprovals: number;
  readonly plansWaiting: number;
  readonly workerAttentions: readonly CockpitWorkerAttention[];
  /** Deduped owner gates still outstanding. Omitted means none. */
  readonly ownerGates?: number;
}): number {
  const workers = input.workerAttentions.filter((item) => item.kind !== 'owner_takeover_available').length;
  return Math.max(0, input.pendingApprovals) + Math.max(0, input.plansWaiting) + workers
    + Math.max(0, input.ownerGates ?? 0);
}

/**
 * Which Worker surfaces stay mounted.
 *
 * Collapsing a Worker is a visual act. The surface it collapsed stays mounted and merely
 * stops being shown, so the live terminal underneath keeps its connection, its replay
 * position and its scrollback instead of being torn down and rebuilt on the next expand. A
 * surface is only dropped once its Worker is no longer there to have one.
 */
export function mountedWorkerSurfaces(
  current: readonly string[],
  expandedWorkerId: string | null,
  liveWorkerIds: readonly string[],
): readonly string[] {
  const live = new Set(liveWorkerIds);
  const kept = current.filter((id) => live.has(id));
  if (expandedWorkerId !== null && live.has(expandedWorkerId) && !kept.includes(expandedWorkerId)) {
    return [...kept, expandedWorkerId];
  }
  return kept;
}

/** True when two mounted-surface lists say the same thing, so React state can be left alone. */
export function sameWorkerSurfaces(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}
